import { useEffect, useState } from "react"
import {
  Box,
  Typography,
  styled,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
} from "@mui/material"
import { BarChart, AxisConfig } from "@mui/x-charts"
import { Wrapper } from "../SettingsSection/SettingsSection.styles"
import * as performanceServices from "../../service/Performance"
import { formatDateForBackend } from "../../util/formatDate"
import { GroupingOptions, UserMetrics } from "../../api/generated"

const StatTitle = styled(Typography)({
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "18px",
  letterSpacing: 0.25,
  color: "var(--brand-text-sec)",
  textAlign: "center",
})

const StatValue = styled(Typography)({
  fontWeight: 700,
  fontSize: 32,
  lineHeight: "normal",
  color: "var(--brand-text-default)",
})

interface Props {
  values: Array<UserMetrics>
  grouping: GroupingOptions
  setGrouping: (grouping: GroupingOptions) => void
  uniqueOpcos: Array<string>
}

const labels = ["Today", "This Week", "This Month", "This Year"]

export default function IndividualPerformance({
  values,
  grouping,
  setGrouping,
  uniqueOpcos,
}: Props) {
  const sortedUniqueOpcos = [...uniqueOpcos].sort((a, b) => a.localeCompare(b))
  const [stats, setStats] = useState<Array<number | string>>(["", "", "", ""])
  const [firstOpco, setFirstOpco] = useState('')
  const opcoOptions = sortedUniqueOpcos.map((opco) => (
    <MenuItem sx={{ textTransform: "capitalize" }} key={opco} value={opco}>
      {opco}
    </MenuItem>
  ))

  const [selectedOpco, setSelectedOpco] = useState<string | undefined>(
    firstOpco,
  )

  useEffect(() => {
    if (sortedUniqueOpcos.length > 0) {
      setFirstOpco(sortedUniqueOpcos[0]);
    }
  }, [sortedUniqueOpcos]);

  useEffect(() => {
    if (firstOpco !== undefined) {
      setSelectedOpco(firstOpco);
    }
  }, [firstOpco]);

  const filterByOpco = (data: typeof values, opco: string | undefined) => {
    if (!opco) return []
    return data.map((item) => ({
      grouping_field: item.grouping_field,
      [opco]: item[opco] || 0,
    }))
  }

  const filteredData = filterByOpco(values, selectedOpco)

  const barChartDataset = filteredData.map((item) => ({
    value: item[selectedOpco || ""] || 0,
    label: item.grouping_field,
  }))

  useEffect(() => {
    const today = formatDateForBackend()
    performanceServices
      .getIndividualPerformance(today, today,GroupingOptions.NONE)
      .then((response) => {
        setStats(response.filter(item => item.opco === selectedOpco).map((item) => item.completed_items))
      })
  }, [selectedOpco])

  return (
    <Wrapper sx={{ gap: "24px", width: "calc(100% - 64px)", padding: "0", background: 'none' }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", }}>
        <Box sx={{ display: "flex", gap: "8px", flexDirection: 'column' }}>
          <Typography sx={{fontSize: '12px', fontWeight: 500, color: 'var(--grey-666)'}}>Time period</Typography>
          <ToggleButtonGroup
              sx={{ "& button": { textTransform: "none" } }}
              value={grouping}
              exclusive
              onChange={(event, value) => {
                // Don't allow deselecting option
                if (value !== null) {
                  setGrouping(value)
                }
              }}
            >
              <ToggleButton value={GroupingOptions.DAY}>
                Last 10 days
              </ToggleButton>
              <ToggleButton value={GroupingOptions.WEEK}>
                Last 10 weeks
              </ToggleButton>
              <ToggleButton value={GroupingOptions.MONTH}>
                Last 12 months
              </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", gap: "8px", flexDirection: 'column' }}>
          <Typography sx={{fontSize: '12px', fontWeight: 500, color: 'var(--grey-666)'}}>Business Unit</Typography>
          {uniqueOpcos.length > 0 && (
            <Select
              value={selectedOpco}
              onChange={({ target: { value } }) => {
                setSelectedOpco(value)
              }}
            >
              {opcoOptions}
            </Select>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          alignSelf: "center",
          backgroundColor: "var(--white)",
          borderRadius: "8px",
          height: "auto",
          width: "100%",
          padding: "24px",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", gap: "64px" }}>
          {stats.map((stat, index) => (
            <Box key={index}>
              <StatTitle>{labels[index]}</StatTitle>
              <StatValue>{stat}</StatValue>
            </Box>
          ))}
        </Box>
        {barChartDataset.length > 0 && (
          <BarChart
            dataset={barChartDataset}
            series={[{ dataKey: "value", color: "var(--bar-color)" }]}
            layout="vertical"
            xAxis={[
              {
                scaleType: "band",
                dataKey: "label",
                categoryGapRatio: 0.5,
              },
            ]}
            yAxis={[
              {
                label: "Invoices",
              },
            ]}
            height={400}
            sx={{
              '& .MuiChartsAxis-root .MuiChartsAxis-line': { stroke: 'var(--grey-CCC)' },
              '& .MuiYAxis-root .MuiYAxis-line': { stroke: 'var(--grey-CCC)' },
              '& .MuiChartsAxis-root .MuiChartsAxis-tick': {stroke: 'var(--grey-CCC)' },
              '& .MuiChartsAxis-root .MuiChartsAxis-tickLabel, & .MuiChartsAxis-root .MuiChartsAxis-label': { fill: "var(--grey-666)", fontSize: '12px' },
            }}
          />
        )}
      </Box>
    </Wrapper>
  )
}
